import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaPlus, FaTrash, FaArrowLeft, FaEdit, FaTimes } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SEO from '../components/SEO';

const AddStudents = () => {
  const [students, setStudents] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingStudent, setEditingStudent] = useState(null);
  const [formData, setFormData] = useState({
    studentname: '',
    linkedin: '',
    batch: '',
    projects: [],
  });
  const [file, setFile] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchStudents();
    fetchProjects();
  }, []);

  const fetchStudents = async () => {
    try {
      const response = await axios.get('/api/students');
      setStudents(response.data);
    } catch (error) {
      console.error('Error fetching students:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axios.get('/getprojects');
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = new FormData();
    data.append('studentname', formData.studentname);
    data.append('linkedin', formData.linkedin);
    data.append('batch', formData.batch);
    
    if (editingStudent) {
      // Edit mode - update student with project mappings
      data.append('studentid', editingStudent.studentid);
      data.append('existingimage', editingStudent.studentimage);
      if (file) data.append('file', file);
      
      // Add selected projects
      formData.projects.forEach(projectId => {
        data.append('projects', projectId);
      });
      
      try {
        await axios.post('/editstudent', data);
        setShowForm(false);
        setEditingStudent(null);
        setFormData({ studentname: '', linkedin: '', batch: '', projects: [] });
        setFile(null);
        fetchStudents();
      } catch (error) {
        console.error('Error editing student:', error);
      }
    } else {
      // Add mode - create entries for each project
      formData.projects.forEach(projectId => {
        data.append('projects', projectId);
      });
      
      if (file) data.append('file', file);

      try {
        await axios.post('/addstudent', data);
        setShowForm(false);
        setFormData({ studentname: '', linkedin: '', batch: '', projects: [] });
        setFile(null);
        fetchStudents();
      } catch (error) {
        console.error('Error adding student:', error);
      }
    }
  };

  const handleEdit = async (student) => {
    setEditingStudent(student);
    
    // Fetch student's current projects
    try {
      const response = await axios.get(`/api/student-projects/${student.studentid}`);
      const projectIds = response.data.map(id => id.toString()); // Convert to strings for checkbox comparison
      
      setFormData({
        studentname: student.studentname,
        linkedin: student.linkedin || '',
        batch: student.batch,
        projects: projectIds,
      });
    } catch (error) {
      console.error('Error fetching student projects:', error);
      setFormData({
        studentname: student.studentname,
        linkedin: student.linkedin || '',
        batch: student.batch,
        projects: [],
      });
    }
    
    setShowForm(true);
  };

  const handleDelete = async (studentid) => {
    if (window.confirm('Are you sure you want to delete this student?')) {
      try {
        await axios.get(`/delete_student?studentid=${studentid}`);
        fetchStudents();
      } catch (error) {
        console.error('Error deleting student:', error);
      }
    }
  };

  return (
    <div className="min-h-screen p-8">
      <SEO title="Manage Students – SDC" description="Add or edit student profiles and link them to the projects they build." canonical="https://sdc.mitsgwalior.in/showstudents" />
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <motion.button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 glass rounded-lg flex items-center space-x-2 hover:bg-white/10"
            whileHover={{ scale: 1.05 }}
          >
            <FaArrowLeft />
            <span>Back</span>
          </motion.button>

          <h1 className="text-3xl font-bold gradient-text">Manage Students</h1>

          <motion.button
            onClick={() => {
              setShowForm(!showForm);
              setEditingStudent(null);
              setFormData({ studentname: '', linkedin: '', batch: '', projects: [] });
              setFile(null);
            }}
            className="px-6 py-2 bg-gradient-to-r from-primary to-secondary rounded-lg flex items-center space-x-2"
            whileHover={{ scale: 1.05 }}
          >
            <FaPlus />
            <span>Add Student</span>
          </motion.button>
        </div>

        {showForm && (
          <motion.form
            onSubmit={handleSubmit}
            className="glass rounded-xl p-6 mb-8"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">{editingStudent ? 'Edit Student' : 'Add New Student'}</h2>
              <button
                type="button"
                onClick={() => {
                  setShowForm(false);
                  setEditingStudent(null);
                  setFormData({ studentname: '', linkedin: '', batch: '', projects: [] });
                  setFile(null);
                }}
                className="text-gray-400 hover:text-white"
              >
                <FaTimes size={20} />
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <input
                type="text"
                placeholder="Student Name"
                value={formData.studentname}
                onChange={(e) => setFormData({ ...formData, studentname: e.target.value })}
                className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-primary"
                required
              />
              <input
                type="url"
                placeholder="LinkedIn URL"
                value={formData.linkedin}
                onChange={(e) => setFormData({ ...formData, linkedin: e.target.value })}
                className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-primary"
              />
              <input
                type="text"
                placeholder="Batch (e.g., 2024)"
                value={formData.batch}
                onChange={(e) => setFormData({ ...formData, batch: e.target.value })}
                className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg focus:outline-none focus:border-primary"
                required
              />
            </div>
            
            <div className="mb-4">
              <label className="block text-sm text-gray-400 mb-2">Select Projects (multiple allowed)</label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-60 overflow-y-auto p-2 bg-white/5 border border-white/10 rounded-lg">
                {projects.map((project) => (
                  <label
                    key={project.projectid}
                    className={`flex items-center space-x-2 p-2 rounded cursor-pointer transition-colors ${
                      formData.projects.includes(project.projectid.toString())
                        ? 'bg-primary/20 border border-primary/50'
                        : 'bg-white/5 hover:bg-white/10 border border-white/10'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={formData.projects.includes(project.projectid.toString())}
                      onChange={(e) => {
                        const projectId = project.projectid.toString();
                        if (e.target.checked) {
                          setFormData({ ...formData, projects: [...formData.projects, projectId] });
                        } else {
                          setFormData({ ...formData, projects: formData.projects.filter(id => id !== projectId) });
                        }
                      }}
                      className="rounded border-gray-400 text-primary focus:ring-primary"
                    />
                    <span className="text-sm">{project.projectname}</span>
                  </label>
                ))}
              </div>
            </div>
            
            <div className="mb-4">
              <label className="block text-sm text-gray-400 mb-2">
                {editingStudent ? 'Change Image (optional)' : 'Upload Image'}
              </label>
              <input
                type="file"
                onChange={(e) => setFile(e.target.files[0])}
                accept="image/*"
                className="text-sm"
              />
            </div>
            <button
              type="submit"
              className="px-6 py-2 bg-gradient-to-r from-primary to-secondary rounded-lg"
            >
              {editingStudent ? 'Update Student' : 'Add Student'}
            </button>
          </motion.form>
        )}

        <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-6">
          {students.map((student) => (
            <motion.div
              key={student.studentid}
              className="glass rounded-xl p-6 text-center"
              whileHover={{ y: -5 }}
            >
              {student.studentimage && (
                <img
                  src={`/files/${student.studentimage}`}
                  alt={student.studentname}
                  className="w-20 h-20 rounded-full mx-auto mb-3 object-cover"
                />
              )}
              <h3 className="text-lg font-bold mb-1">{student.studentname}</h3>
              <p className="text-sm text-gray-400 mb-2">{student.batch}</p>
              {student.projectname && (
                <p className="text-xs text-primary mb-4">{student.projectname}</p>
              )}
              <div className="flex gap-2">
                <button
                  onClick={() => handleEdit(student)}
                  className="flex-1 px-3 py-2 bg-primary/20 hover:bg-primary/30 rounded-lg flex items-center justify-center space-x-2 transition-colors text-sm"
                >
                  <FaEdit />
                  <span>Edit</span>
                </button>
                <button
                  onClick={() => handleDelete(student.studentid)}
                  className="flex-1 px-3 py-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg flex items-center justify-center space-x-2 transition-colors text-sm"
                >
                  <FaTrash />
                  <span>Delete</span>
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AddStudents;
